// The matchmaking backend interface (docs/NETTEST.md): the page asks a backend for a match and gets a
// MatchAssignment; how the backend queues, allocates and hands out seat claims stays inside it.

import type { MatchAssignment, SearchPhase } from '@taxwar/online'

/** Where the edge Worker is: the page's own origin, over http(s) and ws(s). */
export interface EdgeOrigin {
  readonly http: string
  readonly ws: string
}

export interface MatchmakingBackend {
  /** Resolves with the room to join, reporting phases on the way. Aborting cancels the search (and its ticket). */
  findMatch(onPhase: (phase: SearchPhase) => void, signal: AbortSignal): Promise<MatchAssignment>
}

export const MatchmakingFailure = {
  timeout: 'timeout',
  busy: 'busy',
  roomUnavailable: 'room_unavailable',
  canceled: 'canceled',
  edgeRefused: 'edge_refused',
  /** A friend's challenge was already used (or expired): its code pairs one match only. */
  inviteTaken: 'invite_taken',
  connectionLost: 'connection_lost',
  protocol: 'protocol',
} as const
export type MatchmakingFailure = (typeof MatchmakingFailure)[keyof typeof MatchmakingFailure]

/** The search ended without a match (a backend's refusal or timeout, not a bug). */
export class MatchmakingError extends Error {
  override readonly name = 'MatchmakingError'

  constructor(
    readonly failure: MatchmakingFailure,
    message: string,
  ) {
    super(message)
  }
}
